import { useEffect, useState } from "react";
import api from "../services/api";
import loader from "../images/loader.gif";
import { useDictionary } from "../context/useDictionary";
import OrgChartView from "./OrgChartView";

const divisionColorVars = [
  { name: "green_light", hex: "#7dc240" },
  { name: "green", hex: "#099250" },
  { name: "teal", hex: "#0e9384" },
  { name: "cyan", hex: "#088ab2" },
  { name: "blue", hex: "#1570ef" },
  { name: "purple", hex: "#6938ef" },
  { name: "pink", hex: "#e31b54" },
  { name: "orange", hex: "#e04f16" },
  { name: "yellow", hex: "#ca8504" },
];

function hexToRgba(hex, opacity) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${opacity})`;
}

function getOrgChartColors(baseHex, deptIdx = 0) {
  const deptOpacity = 0.8 - deptIdx * 0.1;
  return {
    division: baseHex,
    department: hexToRgba(baseHex, deptOpacity > 0.2 ? deptOpacity : 0.2),
  };
}

export default function OrgChart() {
  const { getDictionaryValueObject, getDictionaryValue } = useDictionary();

  const [searchText, setSearchText] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [divisionColors, setDivisionColors] = useState({});
  const [executiveList, setExecutiveList] = useState([]);

  useEffect(() => {
    async function fetchData() {
      const [divRes, deptRes, teamRes, empRes, execRes] = await Promise.all([
        api.getDivisionsStructure(),
        api.getDepartmentsStructure(),
        api.getTeamsStructure(),
        api.getEmployees(),
        api.getExecutiveLeadership(),
      ]);
      const divs = [...(divRes.data || divRes)]
        .filter((d) => !d.isArchived)
        .sort((a, b) => {
          const aId = a.TWDivisionId;
          const bId = b.TWDivisionId;
          if (aId === null || aId === undefined || aId === "") return 1;
          if (bId === null || bId === undefined || bId === "") return -1;
          if (!isNaN(aId) && !isNaN(bId)) return Number(aId) - Number(bId);
          return aId?.toString().localeCompare(bId?.toString());
        });
      setDivisions(divs);
      setDepartments(deptRes.data || deptRes);
      setTeams(teamRes.data || teamRes);
      setEmployees((empRes.data || empRes).filter((e) => !e.isArchived));
      setExecutiveList(execRes.data || execRes);

      const colorMap = {};
      divs.forEach((div, idx) => {
        colorMap[div.id] = divisionColorVars[idx % divisionColorVars.length].hex;
      });
      setDivisionColors(colorMap);

      setLoading(false);
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="orgchart-loader-container">
        <div className="loader">
          <img alt="loader" src={loader} />
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <OrgChartView
      divisions={divisions}
      departments={departments}
      teams={teams}
      employees={employees}
      divisionColors={divisionColors}
      searchText={searchText}
      setSearchText={setSearchText}
      getDictionaryValue={getDictionaryValue}
      getDictionaryValueObject={getDictionaryValueObject}
      divisionColorVars={divisionColorVars}
      getOrgChartColors={getOrgChartColors}
      executiveList={executiveList}
    />
  );
}
